import { useState } from 'react';
import SEO, { PAGE_SEO } from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { User, Sparkles, Save } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const skinConcernOptions = [
  'Acne', 'Aging', 'Dark Spots', 'Dry Skin', 'Oily Skin', 'Sensitive Skin',
  'Large Pores', 'Wrinkles', 'Hyperpigmentation', 'Redness', 'Dehydration', 'Brightening'
];

const hairConcernOptions = [
  'Hair Loss', 'Hair Thinning', 'Dry Hair', 'Oily Scalp', 'Dandruff', 'Frizzy Hair',
  'Damaged Hair', 'Split Ends', 'Slow Growth', 'Gray Hair', 'Scalp Health'
];

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [skinConcerns, setSkinConcerns] = useState(user?.skin_concerns || []);
  const [hairConcerns, setHairConcerns] = useState(user?.hair_concerns || []);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('skinuse_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };

  const toggleConcern = (list, setList, concern) => {
    if (list.includes(concern)) {
      setList(list.filter(c => c !== concern));
    } else {
      setList([...list, concern]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/profile`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          name,
          skin_concerns: skinConcerns,
          hair_concerns: hairConcerns
        })
      });

      if (res.ok) {
        const updated = await res.json();
        updateUser(updated);
        toast.success('Profile updated successfully!');
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="profile-page">
      <SEO {...PAGE_SEO.profile} url="/profile" />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold mb-2">Profile & Settings</h1>
            <p className="text-muted-foreground">Customize your SkinUse experience</p>
          </div>

          {/* Points Card */}
          <Card className="bg-gold/10 border-none mb-8" data-testid="profile-points">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-sm bg-gold/20 flex items-center justify-center">
                    <Sparkles className="w-7 h-7 text-gold" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Your SkinPoints</p>
                    <p className="font-serif text-3xl font-semibold text-gold">{user?.skin_points || 0}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile Form */}
          <Card className="bg-card border-none shadow-card mb-8" data-testid="profile-form">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <User className="w-6 h-6 text-primary-foreground" />
                  )}
                </div>
                <div>
                  <h2 className="font-serif text-xl font-semibold">Account Details</h2>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Display Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="max-w-md"
                  data-testid="profile-name-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Skin Concerns */}
          <Card className="bg-card border-none shadow-card mb-8" data-testid="skin-concerns-card">
            <CardHeader>
              <h2 className="font-serif text-xl font-semibold">Skin Concerns</h2>
              <p className="text-sm text-muted-foreground">Select your primary skin concerns for personalized recommendations</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {skinConcernOptions.map((concern) => (
                  <div key={concern} className="flex items-center space-x-3">
                    <Checkbox
                      id={`skin-${concern}`}
                      checked={skinConcerns.includes(concern)}
                      onCheckedChange={() => toggleConcern(skinConcerns, setSkinConcerns, concern)}
                      data-testid={`skin-concern-${concern.toLowerCase().replace(' ', '-')}`}
                    />
                    <Label htmlFor={`skin-${concern}`} className="text-sm cursor-pointer">{concern}</Label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Hair Concerns */}
          <Card className="bg-card border-none shadow-card mb-8" data-testid="hair-concerns-card">
            <CardHeader>
              <h2 className="font-serif text-xl font-semibold">Hair Concerns</h2>
              <p className="text-sm text-muted-foreground">Select your primary hair concerns for personalized recommendations</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {hairConcernOptions.map((concern) => (
                  <div key={concern} className="flex items-center space-x-3">
                    <Checkbox
                      id={`hair-${concern}`}
                      checked={hairConcerns.includes(concern)}
                      onCheckedChange={() => toggleConcern(hairConcerns, setHairConcerns, concern)}
                      data-testid={`hair-concern-${concern.toLowerCase().replace(' ', '-')}`}
                    />
                    <Label htmlFor={`hair-${concern}`} className="text-sm cursor-pointer">{concern}</Label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} className="btn-gold gap-2" disabled={saving} data-testid="save-profile">
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
